"use client";

import React, { createContext, useCallback, useContext, useState } from "react";
import { useRouter } from "next/navigation";
import { CURRENCY_COOKIE, type Currency } from "@/lib/currency";
import type { Rates } from "@/lib/fx";

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (next: Currency) => void;
  rates: Rates;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

interface ProviderProps {
  initialCurrency: Currency;
  rates: Rates;
  children: React.ReactNode;
}

export function CurrencyProvider({ initialCurrency, rates, children }: ProviderProps) {
  const router = useRouter();
  const [currency, setCurrencyState] = useState<Currency>(initialCurrency);

  const setCurrency = useCallback(
    (next: Currency) => {
      if (next === currency) return;
      setCurrencyState(next);
      // 1 year cookie, lax to allow normal navigation flows.
      document.cookie = `${CURRENCY_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
      // Re-render server components so prices reflect the new currency.
      router.refresh();
    },
    [currency, router]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
